import { RouterProvider } from "react-router";
import { router } from "./routes";
import { useFaroUser } from "../observability/faro-signals";
import { AuthContext } from "./auth/AuthContext";
import { DataAdapterProvider } from "@shared-core/data/DataAdapterContext";
import { PlatformCapabilitiesProvider } from "@shared-core/capabilities/context";
import { FeedbackCaptureProvider } from "@shared-core/capabilities/feedback-capture";
import { webCapabilities } from "../platform/capabilities";
import { webFeedbackCapture } from "../platform/feedback-capture";
import { PreviewBanner } from "./components/patterns/PreviewBanner";
import type { AuthContextValue } from "./auth/types";
import type { DataAdapter } from "@shared-core/data/adapter";
import { createRealObjectUiAdapter } from "@shared-core/data/api/object-ui";
import { objectManifest } from "@shared-core/data/object-manifest.generated";
import type { ObjectUiAdapter } from "@shared-core/data/object-ui-adapter";
import { ObjectUiAdapterProvider } from "@shared-core/data/ObjectUiAdapterContext";
import type { ObjectManifest } from "@shared-core/domain/object-ui/contract";
import { ObjectManifestProvider } from "@shared-core/domain/object-ui/manifest";
import {
  RecordLayoutDefaultsProvider,
  type RecordLayoutDefaults,
} from "@shared-core/domain/object-ui/record-layout-mode";

/** What One's generic pages run on: the declarations and the adapter over them. */
export interface ObjectUi {
  adapter: ObjectUiAdapter;
  manifest: ObjectManifest;
  /**
   * Which record layout the record page draws when nothing has chosen one, and whether the account menu offers the
   * switch: the mock app with drafts proposes the tabbed record; the live app draws today's.
   */
  recordLayout?: RecordLayoutDefaults;
}

/** The live app: the real adapter over the manifest the modules generate (never a mock or a placeholder). */
export const liveObjectUi: ObjectUi = {
  adapter: createRealObjectUiAdapter(),
  manifest: objectManifest,
};

/** The live app's record layout: today's, with no switch offered (a link's `?layout=tabs` still shows the proposal). */
const TODAY_RECORD_LAYOUT: RecordLayoutDefaults = {
  fallback: "today",
  isSwitchOffered: false,
};

export interface AppProps {
  dataAdapter: DataAdapter;
  authValue: AuthContextValue;
  /** One's adapter and manifest: the live ones unless mock mode passes its own. */
  objectUi?: ObjectUi;
}

/**
 * App shell — wires the resolved data adapter and auth context into the
 * router. `main.tsx` owns the mode selection (dev-bypass, Entra, mock) and
 * hands the already-resolved adapter and auth value to this component.
 */
export function App({
  dataAdapter,
  authValue,
  objectUi = liveObjectUi,
}: AppProps) {
  // Stamp Faro telemetry with the signed-in user (id + role, no PII) so
  // page views, errors and web vitals are attributable. Safe no-op when
  // Faro is disabled. See observability/faro-signals.tsx.
  useFaroUser(authValue.user);
  return (
    <PlatformCapabilitiesProvider capabilities={webCapabilities}>
      <FeedbackCaptureProvider capture={webFeedbackCapture}>
        <DataAdapterProvider adapter={dataAdapter}>
          <ObjectUiAdapterProvider adapter={objectUi.adapter}>
            <ObjectManifestProvider manifest={objectUi.manifest}>
              <RecordLayoutDefaultsProvider
                defaults={objectUi.recordLayout ?? TODAY_RECORD_LAYOUT}
              >
                <AuthContext.Provider value={authValue}>
                  {/* Preview-only amber bar, pinned above the logo/nav. Renders nothing
                  in the live app (readPreviewContext() returns null). */}
                  <PreviewBanner />
                  <RouterProvider router={router} />
                </AuthContext.Provider>
              </RecordLayoutDefaultsProvider>
            </ObjectManifestProvider>
          </ObjectUiAdapterProvider>
        </DataAdapterProvider>
      </FeedbackCaptureProvider>
    </PlatformCapabilitiesProvider>
  );
}
